import { forwardRef, useEffect, useImperativeHandle } from 'react';

import { ajvResolver } from '@hookform/resolvers/ajv';
import { JSONSchemaType } from 'ajv';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { FormContainer, TextField } from '~components';
import { useFormContainerState } from '~hooks';
import { FormWrapperRefProps } from '~interfaces/refProps';

import schema from './skcNfcTagSchema.json';
import NfcTag from '../../interfaces/nfcTag';
import SkcKeysService from '../../services/skcKeysService';

const service = new SkcKeysService();

interface SkcNfcTagFormProps {
	id: string;
	onSubmit?: (value: NfcTag) => void;
}

const SkcNfcTagForm = forwardRef<FormWrapperRefProps, SkcNfcTagFormProps>(
	({ id, onSubmit }, ref) => {
		const { t } = useTranslation();

		const { data, isLoading } = useSWR([service.basePath, id], ([_, arg]) => service.getKeyId(arg));

		const { setDisabled } = useFormContainerState();
		const { getValues, control, formState, reset } = useForm<NfcTag>({
			defaultValues: data,
			mode: 'onChange',
			resolver: ajvResolver(schema as JSONSchemaType<any>),
		});

		useEffect(() => {
			if (data) {
				// Used to reset the useform, otherwise the page won't properly reload
				reset(data);
			}
		}, [data, reset]);

		useImperativeHandle(
			ref,
			() => ({
				onSubmit: () => onSubmit?.(getValues()),
			}),
			[onSubmit, getValues]
		);

		useEffect(() => {
			setDisabled?.(!(formState.isValid && formState.isDirty));
		}, [formState.isValid, formState.isDirty, setDisabled]);

		const getErrorText = (error: FieldError | undefined): string | undefined => {
			if (error?.type === 'minLength') {
				return t('ui.error.message.minLength');
			}

			return error?.message;
		};

		return (
			<FormContainer loading={isLoading}>
				<Controller
					name='label'
					control={control}
					render={({ field, fieldState }) => (
						<TextField
							{...field}
							required
							label={t('name')}
							error={fieldState.error != null}
							helperText={getErrorText(fieldState.error)}
							slotProps={{
								htmlInput: {
									maxLength: schema.properties.label.maxLength,
								},
							}}
						/>
					)}
				/>
			</FormContainer>
		);
	}
);

export default SkcNfcTagForm;
